import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Modal,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import React from 'react';
import { StyledHeading, StyledTypography } from '../../../components/styles/styles';
import { FAQ_API_PREFIX, buildPublicAssetImagePlaceholder } from './faqAdminUtils';

interface FAQImageHelperModalProps {
  open: boolean;
  onClose: () => void;
  tab: 0 | 1;
  onTabChange: (tab: 0 | 1) => void;
  assetAlt: string;
  onAssetAltChange: (value: string) => void;
  publicAssetList: string[] | null;
  publicAssetFilter: string;
  onPublicAssetFilterChange: (value: string) => void;
  onReloadPublicAssets: () => void;
  apiImages: { relativePath: string; contentType: string }[];
  onReloadApiImages: () => void;
  desiredFileName: string;
  onDesiredFileNameChange: (value: string) => void;
  selectedUploadFile: File | null;
  onSelectUploadFile: (file: File | null) => void;
  uploading: boolean;
  onUpload: (file: File) => Promise<void>;
  onInsertPlaceholder: (placeholder: string) => void;
}

const FAQImageHelperModal: React.FC<FAQImageHelperModalProps> = ({
  open,
  onClose,
  tab,
  onTabChange,
  assetAlt,
  onAssetAltChange,
  publicAssetList,
  publicAssetFilter,
  onPublicAssetFilterChange,
  onReloadPublicAssets,
  apiImages,
  onReloadApiImages,
  desiredFileName,
  onDesiredFileNameChange,
  selectedUploadFile,
  onSelectUploadFile,
  uploading,
  onUpload,
  onInsertPlaceholder,
}) => {
  const insertAndClose = (placeholder: string) => {
    onInsertPlaceholder(placeholder);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(1100px, 95vw)',
          bgcolor: 'background.paper',
          p: 2,
          borderRadius: 2,
          boxShadow: 4,
        }}
      >
        <StyledHeading headerVariant="subheading">Insert Image</StyledHeading>
        <Tabs value={tab} onChange={(_, v) => onTabChange(v)} sx={{ mb: 2 }}>
          <Tab label="Public assets (/assets/faq)" />
          <Tab label="GCS Images (/api/images)" />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
              <TextField
                size="small"
                label="Alt text"
                value={assetAlt}
                onChange={(e) => onAssetAltChange(e.target.value)}
              />
              <Button size="small" onClick={onReloadPublicAssets}>
                Reload
              </Button>
            </Box>
            {publicAssetList ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: 1,
                  maxHeight: '60vh',
                  overflow: 'auto',
                }}
              >
                {publicAssetList
                  .filter((f) => (publicAssetFilter ? f.includes(publicAssetFilter) : true))
                  .map((f) => (
                    <Card
                      key={f}
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        insertAndClose(buildPublicAssetImagePlaceholder(f, assetAlt || f))
                      }
                    >
                      <CardMedia
                        component="img"
                        image={`/assets/faq/${f}`}
                        alt={f}
                        sx={{ height: 120, objectFit: 'contain', bgcolor: '#fafafa' }}
                      />
                      <CardContent>
                        <StyledTypography
                          tone="default"
                          fontSize="0.8rem"
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {f}
                        </StyledTypography>
                      </CardContent>
                    </Card>
                  ))}
              </Box>
            ) : (
              <Box>
                <StyledTypography tone="default" sx={{ mb: 1 }}>
                  No manifest found at /assets/faq/manifest.json. Enter a filename manually:
                </StyledTypography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Filename (e.g. login.png)"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const input = (e.target as HTMLInputElement).value.trim();
                        if (input) {
                          insertAndClose(
                            buildPublicAssetImagePlaceholder(input, assetAlt || input),
                          );
                        }
                      }
                    }}
                  />
                  <TextField
                    size="small"
                    label="Filter"
                    value={publicAssetFilter}
                    onChange={(e) => onPublicAssetFilterChange(e.target.value)}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
              <Button size="small" onClick={onReloadApiImages}>
                Search
              </Button>
              <TextField
                size="small"
                label="Alt text"
                value={assetAlt}
                onChange={(e) => onAssetAltChange(e.target.value)}
              />
              <TextField
                size="small"
                label="Filename"
                placeholder="my-image.png"
                value={desiredFileName}
                onChange={(e) => onDesiredFileNameChange(e.target.value)}
                disabled={uploading}
              />
              <input
                type="file"
                accept="image/*"
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  onSelectUploadFile(f);
                  if (f && !desiredFileName) onDesiredFileNameChange(f.name);
                }}
              />
              <Button
                size="small"
                variant="contained"
                disabled={!selectedUploadFile || uploading}
                onClick={async () => {
                  if (!selectedUploadFile) return;
                  await onUpload(selectedUploadFile);
                }}
              >
                {uploading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} /> Uploading
                  </Box>
                ) : (
                  'Upload'
                )}
              </Button>
            </Box>
            {desiredFileName &&
              apiImages.some((i) => i.relativePath === `${FAQ_API_PREFIX}/${desiredFileName}`) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1,
                    border: '1px solid #f0ad4e',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <CardMedia
                    component="img"
                    image={`/api/arbitraryImages/${FAQ_API_PREFIX}/${desiredFileName}`}
                    alt={desiredFileName}
                    sx={{ width: 96, height: 64, objectFit: 'contain', bgcolor: '#fafafa' }}
                  />
                  <StyledTypography tone="default" fontSize="0.9rem" sx={{ color: '#a15c00' }}>
                    Warning: {FAQ_API_PREFIX}/{desiredFileName} exists and will be replaced.
                  </StyledTypography>
                </Box>
              )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 1,
                maxHeight: '60vh',
                overflow: 'auto',
              }}
            >
              {apiImages.map((img) => (
                <Card
                  key={img.relativePath}
                  sx={{ cursor: 'pointer' }}
                  onClick={() =>
                    insertAndClose(
                      `#img:/api/images/${img.relativePath}:${assetAlt || img.relativePath}#`,
                    )
                  }
                >
                  <CardMedia
                    component="img"
                    image={`/api/arbitraryImages/${img.relativePath}`}
                    alt={img.relativePath}
                    sx={{ height: 120, objectFit: 'contain', bgcolor: '#fafafa' }}
                  />
                  <CardContent>
                    <StyledTypography
                      tone="default"
                      fontSize="0.8rem"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {img.relativePath}
                    </StyledTypography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default FAQImageHelperModal;
